import _isNil from 'lodash/isNil';
import _map from 'lodash/map';
import _toFinite from 'lodash/toFinite';
import _toLower from 'lodash/toLower';
// serializr
import {
  SKIP, primitive, custom, list, object, raw,
  createSimpleSchema, deserialize,
} from 'serializr';

// ---- Schemas placed on top because of dependencies ---- //
// Address & location
const addressSchema = createSimpleSchema({
  city: primitive(),
  country: primitive(),
  extra: primitive(),
  street: primitive(),
  zip_code: primitive(),
});

const cpmAddressSchema = createSimpleSchema({
  city: primitive(),
  country: primitive(),
  extra: primitive(),
  street: primitive(),
  zipCode: primitive(),
});

const locationSchema = createSimpleSchema({
  latitude: primitive(),
  longitude: primitive(),
});

// Operators
export const operatorSchema = createSimpleSchema({
  code: primitive(),
  country: primitive(),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  name: primitive(),
});

// ---- Models schemas ---- //
// Alertings
export const alertingSchema = createSimpleSchema({
  id: custom(() => SKIP, (v) => _toFinite(v)),
  medias: list(primitive()),
  type: primitive(),
});

// Apns
export const apnSchema = createSimpleSchema({
  id: custom(() => SKIP, (v) => _toFinite(v)),
  identifier: primitive(),
  login: primitive(),
  name: primitive(),
  password: primitive(),
});

// Async
const asyncOperationDeviceSchema = createSimpleSchema({
  code: primitive(),
  desc: primitive(),
  device_id: custom((v) => v, (v) => _toFinite(v)),
  ignored: primitive(),
  status: primitive(),
});

export const asyncOperationSchema = createSimpleSchema({
  devices: list(object(asyncOperationDeviceSchema)),
  id: custom((v) => v, (v) => _toFinite(v)),
  location: primitive(),
  status: primitive(),
});

// Companies
export const companySchema = createSimpleSchema({
  children: custom(() => SKIP, (v) => deserialize(companySchema, v)),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  name: primitive(),
  parent_id: custom((v) => v, () => SKIP),
});

// Customers
const customerAccessWayRightSchema = createSimpleSchema({
  begin: primitive(),
  enabled: primitive(),
  end: primitive(),
  id: custom((v) => v, (v) => _toFinite(v)),
  planning_id: custom((v) => v, (v) => (v ? _toFinite(v) : null)),
  scenario_trigger_id: custom((v) => v, (v) => _toFinite(v)),
});

export const customerAccessWaySchema = createSimpleSchema({
  desc: primitive(),
  enabled: primitive(),
  id: custom((v) => v, (v) => _toFinite(v)),
  rights: list(object(customerAccessWayRightSchema)),
  type: primitive(),
});

const customerStateSchema = createSimpleSchema({
  date: custom(() => SKIP, (v) => v),
  delta: custom(() => SKIP, (v) => _toFinite(v)),
  inside_quantity: custom((v) => v, (v) => _toFinite(v)),
  is_computed: custom(() => SKIP, (v) => v),
  outside_quantity: custom((v) => v, (v) => _toFinite(v)),
  unknown_quantity: custom(() => SKIP, (v) => _toFinite(v)),
});

const customerMetadataSchema = createSimpleSchema({
  booking_id: primitive(),
  booking_usage: custom((v) => v, (v) => _toLower(v)),
});

export const customerSchema = createSimpleSchema({
  access_ways: list(object(customerAccessWaySchema)),
  async_operation_id: custom(() => SKIP, (v) => (v ? _toFinite(v) : null)),
  comments: primitive(),
  email: primitive(),
  entity_id: custom((v) => v, (v) => _toFinite(v)),
  firstname: primitive(),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  is_synchronized: primitive(),
  last_access: custom(() => SKIP, (v) => v),
  lastname: primitive(),
  metadata: object(customerMetadataSchema),
  restricted_by_antipassback_entrance: primitive(),
  restricted_by_antipassback_exit: primitive(),
  state: object(customerStateSchema),
  sync_status: custom(() => SKIP, (v) => v),
  weight: primitive(),
});

export const customerDailyCodesSchema = createSimpleSchema({
  begin: primitive(),
  end: primitive(),
  entity_id: primitive(),
  prefix_lastname: primitive(),
});

// Devices
export const deviceConfigSchema = createSimpleSchema({
  async_operation_id: custom(() => SKIP, (v) => (v ? _toFinite(v) : null)),
  default_operator_id: custom((v) => v, (v) => (v ? _toFinite(v) : null)),
  equipments_network_ip: primitive(),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  is_synchronized: primitive(),
  log_level: primitive(),
  monitoring_timer: custom((v) => v, (v) => _toFinite(v)),
  tcp_port: custom((v) => v, (v) => _toFinite(v)),
  udp_port: custom((v) => v, (v) => _toFinite(v)),
  ignore_plate_duration: custom((v) => v, (v) => _toFinite(v)),
  operators_blacklist: list(primitive()),
  operators_whitelist_countries: list(primitive()),
  periodic_synchronization_timer: custom((v) => v, (v) => _toFinite(v)),
  server_url: primitive(),
  sync_status: custom(() => SKIP, (v) => v),
});
const deserializeDeviceConfig = (data) => deserialize(deviceConfigSchema, data);

const deviceDistributionSchema = createSimpleSchema({
  comment: primitive(),
  date: primitive(),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  type: primitive(),
  version: primitive(),
});
const deserializeDeviceDistribution = (data) => deserialize(deviceDistributionSchema, data);

export const deviceModelSchema = createSimpleSchema({
  id: custom(() => SKIP, (v) => _toFinite(v)),
  distribution_required_types: raw(),
  name: primitive(),
  productible: primitive(),
  type: primitive(),
  version: primitive(),
});
const deserializeDeviceModel = (data) => deserialize(deviceModelSchema, data);

const deviceSlaveRelaySchema = createSimpleSchema({
  code: custom(() => SKIP, (v) => v),
  doors: list(primitive()),
  id: custom((v) => v, (v) => _toFinite(v)),
});

const deviceSlaveInputSchema = createSimpleSchema({
  code: custom(() => SKIP, (v) => v),
  id: custom((v) => v, (v) => _toFinite(v)),
  sources: list(primitive()),
});

const deviceSlaveTriggerEquipmentSchema = createSimpleSchema({
  brand: custom(() => SKIP, (v) => v),
  id: custom((v) => v, (v) => _toFinite(v)),
  ip: primitive(),
  mac: primitive(),
  model: custom(() => SKIP, (v) => v),
  name: primitive(),
  protocol: custom(() => SKIP, (v) => v),
  status: custom(() => SKIP, (v) => v),
  type: custom(() => SKIP, (v) => v),
});

const deviceSlaveTriggerSchema = createSimpleSchema({
  equipment: object(deviceSlaveTriggerEquipmentSchema),
  fallback_trigger_id: custom((v) => v, (v) => (v ? _toFinite(v) : null)),
  id: custom((v) => v, (v) => _toFinite(v)),
  protocols: custom(() => SKIP, (v) => v),
  type: primitive(),
});

export const deviceSlaveSchema = createSimpleSchema({
  async_operation_id: custom(() => SKIP, (v) => (v ? _toFinite(v) : null)),
  company_id: custom((v) => v || SKIP, (v) => (v ? _toFinite(v) : null)),
  device: custom(() => SKIP, (v) => (v ? { id: _toFinite(v.id) } : null)),
  device_id: custom(() => SKIP, (v) => _toFinite(v)),
  device_model: custom(() => SKIP, (v) => (v ? deserializeDeviceModel(v) : null)),
  device_production_batch_id: custom(() => SKIP, (v) => _toFinite(v)),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  inputs: list(object(deviceSlaveInputSchema)),
  is_synchronized: primitive(),
  name: custom((v) => v || SKIP, (v) => v),
  relays: list(object(deviceSlaveRelaySchema)),
  serial: custom(() => SKIP, (v) => v),
  sync_status: custom(() => SKIP, (v) => v),
  triggers: list(object(deviceSlaveTriggerSchema)),
  virtual_trigger_types: custom(() => SKIP, (v) => v),
});
const deserializeDeviceSlave = (data) => deserialize(deviceSlaveSchema, data);

const deviceInfoSchema = createSimpleSchema({
  modem_imei: primitive(),
  modem_manufacturer: primitive(),
  modem_model: primitive(),
  modem_revision: primitive(),
  network_access_technology: primitive(),
  network_level: primitive(),
  network_operator: object(operatorSchema),
});
const deserializeDeviceInfo = (data) => deserialize(deviceInfoSchema, data);

export const deviceInstallationDeviceSchema = createSimpleSchema({
  disabled: primitive(),
  name: primitive(),
  since: custom(() => SKIP, (v) => v),
});

const deviceInstallationSchema = createSimpleSchema({
  device: object(deviceInstallationDeviceSchema),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  name: primitive(),
});
const deserializeDeviceInstallation = (data) => deserialize(deviceInstallationSchema, data);

const deviceSimcardSchema = createSimpleSchema({
  group: raw(),
  iccid: primitive(),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  number: primitive(),
  status: primitive(),
});
const deserializeDeviceSimcard = (data) => deserialize(deviceSimcardSchema, data);

const deviceSimcardHistorySchema = createSimpleSchema({
  begin: primitive(),
  end: primitive(),
  iccid: primitive(),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  number: primitive(),
});
const deserializeDeviceSimcardHistory = (data) => deserialize(deviceSimcardHistorySchema, data);

export const deviceSyncStatusSchema = createSimpleSchema({
  allotments: primitive(),
  antipassbacks: primitive(),
  apns: primitive(),
  config: primitive(),
  customers: primitive(),
  entities: primitive(),
  installations: primitive(),
  parking_categories: primitive(),
  plannings: primitive(),
  scenarios: primitive(),
  slaves: primitive(),
  tasks: primitive(),
});
const deserializeDeviceSyncStatus = (data) => deserialize(deviceSyncStatusSchema, data);

export const deviceSchema = createSimpleSchema({
  company_id: custom((v) => v, (v) => (v ? _toFinite(v) : null)),
  config: custom(() => SKIP, (v) => (v ? deserializeDeviceConfig(v) : null)),
  device_distribution_maps: custom(() => SKIP, (v) => _map(v, (dMap) => deserializeDeviceDistribution(dMap.distribution))),
  device_model: custom(() => SKIP, (v) => (v ? deserializeDeviceModel(v) : null)),
  device_production_batch_id: custom(() => SKIP, (v) => _toFinite(v)),
  device_slaves: custom(() => SKIP, (v) => deserializeDeviceSlave(v)),
  disabled: custom(() => SKIP, (v) => v),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  info: custom(() => SKIP, (v) => deserializeDeviceInfo(v)),
  installation: custom(() => SKIP, (v) => (v ? deserializeDeviceInstallation(v) : null)),
  ip_address: custom(() => SKIP, (v) => v),
  last_comm_date: custom(() => SKIP, (v) => v),
  rollout_in_progress: custom(() => SKIP, (v) => v),
  serial: custom(() => SKIP, (v) => v),
  simcard: custom(() => SKIP, (v) => (v ? deserializeDeviceSimcard(v) : null)),
  simcard_history: custom(() => SKIP, (v) => deserializeDeviceSimcardHistory(v)),
  sync_status: custom(() => SKIP, (v) => deserializeDeviceSyncStatus(v)),
  timeout: custom(() => SKIP, (v) => v),
});

export const deviceDistributionHistorySchema = createSimpleSchema({
  begin: primitive(),
  distribution: object(deviceDistributionSchema),
  end: primitive(),
  id: custom(() => SKIP, (v) => _toFinite(v)),
});

export const deviceSmsSchema = createSimpleSchema({
  data: custom((v) => v, () => SKIP),
  date: custom(() => SKIP, (v) => v),
  delivery_status: custom(() => SKIP, (v) => v),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  respond_to: custom((v) => v, () => SKIP),
  type: primitive(),
});

const deviceBatchDeviceSchema = createSimpleSchema({
  id: custom(() => SKIP, (v) => _toFinite(v)),
  serial: primitive(),
});
const deserializeDeviceBatchDevice = (data) => deserialize(deviceBatchDeviceSchema, data);

export const deviceBatchSchema = createSimpleSchema({
  author_id: custom(() => SKIP, (v) => _toFinite(v)),
  commit_date: custom(() => SKIP, (v) => v),
  committer_id: custom(() => SKIP, (v) => _toFinite(v)),
  date: primitive(),
  device_model_id: custom((v) => v, (v) => _toFinite(v)),
  devices: custom(() => SKIP, (v) => deserializeDeviceBatchDevice(v)),
  distributions: raw(),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  name: primitive(),
  status: custom(() => SKIP, (v) => v),
  volume: custom((v) => (v ? _toFinite(v) : null), () => SKIP),
});

export const deviceBatchUserSchema = createSimpleSchema({
  firstname: primitive(),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  lastname: primitive(),
});

export const deviceLogSchema = createSimpleSchema({
  date: primitive(),
  desc: primitive(),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  level: primitive(),
  origin: primitive(),
  tag: primitive(),
  type: primitive(),
});

// Distributions
const distributionFileSchema = createSimpleSchema({
  name: primitive(),
  size: primitive(),
  uploaded: custom(() => SKIP, (v) => v),
});

export const distributionSchema = createSimpleSchema({
  comment: primitive(),
  date: primitive(),
  devices_quantity: custom(() => SKIP, (v) => _toFinite(v)),
  disabled: primitive(),
  files: list(object(distributionFileSchema)),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  rollouts_quantity: custom(() => SKIP, (v) => _toFinite(v)),
  type: primitive(),
  version: primitive(),
});
const deserializeDistribution = (data) => deserialize(distributionSchema, data);

// Doors
const doorScenarioSchema = createSimpleSchema({
  id: custom(() => SKIP, (v) => _toFinite(v)),
  name: primitive(),
});
const deserializeDoorScenario = (data) => deserialize(doorScenarioSchema, data);

export const doorSchema = createSimpleSchema({
  id: custom(() => SKIP, (v) => _toFinite(v)),
  name: primitive(),
  relays: custom(() => SKIP, (v) => v),
  scenario: custom(() => SKIP, (v) => (v ? deserializeDoorScenario(v) : null)),
  scenario_id: primitive(),
  type: primitive(),
  usage: primitive(),
});

// Entities
export const entitySchema = createSimpleSchema({
  allow_listing_of_onepark_bookings: primitive(),
  async_operation_id: custom(() => SKIP, (v) => (v ? _toFinite(v) : null)),
  capacity: primitive(),
  company_id: primitive(),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  is_on_demand: primitive(),
  is_synchronized: primitive(),
  excluded_doors: list(primitive()),
  name: primitive(),
  public_name: primitive(),
  product: primitive(),
  sync_status: custom(() => SKIP, (v) => v),
  type: custom(() => SKIP, (v) => v),
});
const deserializeEntity = (data) => deserialize(entitySchema, data);

// Equipments
export const equipmentSchema = createSimpleSchema({
  brand: primitive(),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  ip: primitive(),
  mac: primitive(),
  model: primitive(),
  name: primitive(),
  protocol: primitive(),
  status: primitive(),
  type: primitive(),
});

// Installations
const installationDeviceSchema = createSimpleSchema({
  id: custom(() => SKIP, (v) => _toFinite(v)),
  name: primitive(),
});
const deserializeInstallationDevice = (data) => deserialize(installationDeviceSchema, data);

const installationAntipassbackSchema = createSimpleSchema({
  enabled_entrance: primitive(),
  enabled_exit: primitive(),
  required_entrance: primitive(),
  required_exit: primitive(),
});

const installationAsyncOperationDeviceSchema = createSimpleSchema({
  async_date: primitive(),
  async_status: primitive(), // Status asynchrone: success or pending
  device_id: custom(() => SKIP, (v) => _toFinite(v)),
  sync_error_code: primitive(),
  sync_error_desc: primitive(),
  sync_error_ignored: primitive(),
  sync_status: primitive(), // Status synchrone: success, pending or error
});
export const installationAsyncOperationSchema = createSimpleSchema({
  author_id: custom((v) => v, (v) => _toFinite(v)),
  date: primitive(),
  devices: list(object(installationAsyncOperationDeviceSchema)),
  id: custom((v) => v, (v) => _toFinite(v)),
  method: primitive(),
  resource_id: custom((v) => v, (v) => _toFinite(v)),
  resource_type: primitive(),
  rollback_status: primitive(),
  status: primitive(),
});
const deserializeInstallationAsyncOperation = (data) => deserialize(installationAsyncOperationSchema, data);

export const installationSchema = createSimpleSchema({
  address: object(addressSchema),
  antipassback: object(installationAntipassbackSchema),
  async_operation_id: custom(() => SKIP, (v) => (v ? _toFinite(v) : null)),
  async_operations_error_quantity: custom(() => SKIP, (v) => (!_isNil(v) ? _toFinite(v) : null)),
  async_operations_success_rate: custom(() => SKIP, (v) => (!_isNil(v) ? _toFinite(v) : null)),
  company_id: custom((v) => v, (v) => (v ? _toFinite(v) : null)),
  devices: custom(() => SKIP, (v) => deserializeInstallationDevice(v)),
  entities: custom(() => SKIP, (v) => deserializeEntity(v)),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  inserted_at: custom(() => SKIP, (v) => v),
  is_synchronized: primitive(),
  last_error_async_operation: custom(() => SKIP, (v) => (v ? deserializeInstallationAsyncOperation(v) : null)),
  last_success_async_operation: custom(() => SKIP, (v) => (v ? deserializeInstallationAsyncOperation(v) : null)),
  location: object(locationSchema),
  name: primitive(),
  production: primitive(),
  sync_status: custom(() => SKIP, (v) => v),
  timezone: primitive(),
});

const installationLogCustomerSchema = createSimpleSchema({
  firstname: primitive(),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  lastname: primitive(),
});

const installationLogScenarioLogExecActionSchema = createSimpleSchema({
  delay: primitive(),
  expression: primitive(),
  success: primitive(),
  time: raw(),
});
// Need this for self reference
installationLogScenarioLogExecActionSchema.props.next = object(installationLogScenarioLogExecActionSchema);

const installationLogScenarioLogExecSchema = createSimpleSchema({
  actions: list(object(installationLogScenarioLogExecActionSchema)),
  conflicts: raw(),
  input_condition: primitive(),
  input_trigger: primitive(),
  priority: primitive(),
});

const installationLogScenarioLogSchema = createSimpleSchema({
  date: primitive(),
  exec: object(installationLogScenarioLogExecSchema),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  scenario_id: custom(() => SKIP, (v) => (v ? _toFinite(v) : null)),
  scenario_name: primitive(),
  status: primitive(),
  success_condition: primitive(),
});

const installationLogTaskSchema = createSimpleSchema({
  id: custom(() => SKIP, (v) => _toFinite(v)),
  name: primitive(),
});

const installationLogTriggerEquipmentSchema = createSimpleSchema({
  id: custom(() => SKIP, (v) => _toFinite(v)),
  name: primitive(),
  type: primitive(),
});

const installationLogTriggerSchema = createSimpleSchema({
  equipment: object(installationLogTriggerEquipmentSchema),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  type: primitive(),
});

export const installationLogSchema = createSimpleSchema({
  access_way_id: custom(() => SKIP, (v) => (v ? _toFinite(v) : null)),
  customer: object(installationLogCustomerSchema),
  date: primitive(),
  desc: primitive(),
  read_desc: primitive(),
  device_id: custom(() => SKIP, (v) => _toFinite(v)),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  right_id: custom(() => SKIP, (v) => (v ? _toFinite(v) : null)),
  ignored: primitive(),
  scenario_log: object(installationLogScenarioLogSchema),
  scenario_trigger_type: primitive(),
  task: object(installationLogTaskSchema),
  trigger: object(installationLogTriggerSchema),
  type: primitive(),
  value: primitive(),
});

// Permissions
export const permissionSchema = createSimpleSchema({
  name: primitive(),
  product: primitive(),
});

export const userPermissionSchema = createSimpleSchema({
  company_id: primitive(),
  permissions: list(primitive()),
  profile_id: primitive(),
});

// Plannings
const planningSlotElementSchema = createSimpleSchema({
  day_of_week: primitive(),
  time: primitive(),
});

const planningSlotSchema = createSimpleSchema({
  end: object(planningSlotElementSchema),
  start: object(planningSlotElementSchema),
});

export const planningSchema = createSimpleSchema({
  async_operation_id: custom(() => SKIP, (v) => (v ? _toFinite(v) : null)),
  enabled: primitive(),
  entity_id: custom((v) => v, (v) => _toFinite(v)),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  is_synchronized: primitive(),
  name: primitive(),
  slots: list(object(planningSlotSchema)),
  sync_status: custom(() => SKIP, (v) => v),
});

// Profiles
export const profileSchema = createSimpleSchema({
  id: custom(() => SKIP, (v) => _toFinite(v)),
  name: primitive(),
  permissions: list(primitive()),
  product: primitive(),
});

// Rights
export const rightSchema = createSimpleSchema({
  begin: primitive(),
  enabled: primitive(),
  end: primitive(),
  planning_id: primitive(),
  scenario_trigger_id: primitive(),
});

// Rollouts
export const rolloutSchema = createSimpleSchema({
  date: primitive(),
  device_ids: custom((v) => v, () => SKIP),
  devices_quantities: custom(() => SKIP, (v) => v),
  distribution: custom(() => SKIP, (v) => deserializeDistribution(v)),
  distribution_id: custom((v) => v, () => SKIP),
  force_complete_update: primitive(),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  statuses: custom(() => SKIP, (v) => v),
});

const rolloutDeviceDataSchema = createSimpleSchema({
  id: custom(() => SKIP, (v) => _toFinite(v)),
  serial: primitive(),
  timeout: primitive(),
});

export const rolloutDeviceSchema = createSimpleSchema({
  device: object(rolloutDeviceDataSchema),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  statuses: raw(),
});

// Scenarios
const scenarioInputSchema = createSimpleSchema({
  expression: primitive(),
  id: custom(() => SKIP, (v) => _toFinite(v)),
});

const scenarioActionSchema = createSimpleSchema({
  delay: primitive(),
  duration: primitive(),
  expression: primitive(),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  input_condition: object(scenarioInputSchema),
  input_trigger: object(scenarioInputSchema),
});
// Need this for self reference
scenarioActionSchema.props.next = object(scenarioActionSchema);

const scenarioTriggerSchema = createSimpleSchema({
  enabled: primitive(),
  id: custom((v) => v, (v) => _toFinite(v)),
  on_demand_enabled: primitive(),
  on_demand_validation_trigger_id: primitive(),
  trigger_id: custom((v) => v, (v) => _toFinite(v)),
  type: primitive(),
});

export const scenarioSchema = createSimpleSchema({
  actions: list(object(scenarioActionSchema)),
  async_operation_id: custom(() => SKIP, (v) => (v ? _toFinite(v) : null)),
  device_id: custom((v) => v, (v) => _toFinite(v)),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  input_condition: object(scenarioInputSchema),
  input_trigger: object(scenarioInputSchema),
  involved_in_antipassback: primitive(),
  is_synchronized: primitive(),
  name: primitive(),
  priority: primitive(),
  scenario_triggers: list(object(scenarioTriggerSchema)),
  self_exclusion: primitive(),
  success_condition: primitive(),
  sync_status: custom(() => SKIP, (v) => v),
});

// Simcards
const simcardDeviceHistorySchema = createSimpleSchema({
  begin: primitive(),
  end: primitive(),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  serial: primitive(),
});
const deserializeSimcardDeviceHistory = (data) => deserialize(simcardDeviceHistorySchema, data);

export const simcardGroupSchema = createSimpleSchema({
  apns: custom(() => SKIP, (v) => v),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  name: primitive(),
});
const deserializeSimcardGroup = (data) => deserialize(simcardGroupSchema, data);

export const simcardSchema = createSimpleSchema({
  device: custom(() => SKIP, (v) => (v ? { id: _toFinite(v.id) } : null)),
  device_history: custom(() => SKIP, (v) => deserializeSimcardDeviceHistory(v)),
  group: custom(() => SKIP, (v) => deserializeSimcardGroup(v)),
  group_id: custom((v) => v, (v) => _toFinite(v)),
  iccid: primitive(),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  number: primitive(),
  status: primitive(),
});

// Sources
export const sourceSchema = createSimpleSchema({
  id: custom(() => SKIP, (v) => _toFinite(v)),
  inputs: custom(() => SKIP, (v) => v),
  name: primitive(),
  type: primitive(),
});

// Users
export const userCallbackSchema = createSimpleSchema({
  subscriptions: list(primitive()),
  uri: primitive(),
  version: primitive(),
});

export const userAlertingSchema = createSimpleSchema({
  alerting_id: primitive(),
  config: raw(),
  media: primitive(),
});

const userStripeDetailsSchema = createSimpleSchema({
  id: primitive(),
});

const userPaymentMethodsSchema = createSimpleSchema({
  brand: primitive(),
  expMonth: primitive(),
  expYear: primitive(),
  last4: primitive(),
  provider: custom(() => SKIP, (v) => v),
  stripeDetails: object(userStripeDetailsSchema),
});

export const userSchema = createSimpleSchema({
  companyName: primitive(),
  callback: object(userCallbackSchema),
  comment: primitive(),
  company_id: primitive(),
  currentPassword: custom((v) => v, () => SKIP),
  email: primitive(),
  features: custom(() => SKIP, (v) => _toLower(v)),
  firstname: primitive(),
  id: custom(() => SKIP, (v) => _toFinite(v)),
  invoiceAddress: custom((v) => v, (v) => (v ? deserialize(cpmAddressSchema, v) : null)),
  lastname: primitive(),
  locale: primitive(),
  nationalIdentifier: primitive(),
  paymentMethods: list(object(userPaymentMethodsSchema)),
  password: primitive(),
  permissions: list(object(userPermissionSchema)),
  phone: primitive(),
  public_id: custom(() => SKIP, (v) => v),
  user_alertings: list(object(userAlertingSchema)),
  rollback_strategy: primitive(),
});

export const meSchema = createSimpleSchema({
  locale: primitive(),
  firstname: primitive(),
  lastname: primitive(),
  email: primitive(),
  currentPassword: custom((v) => v, () => SKIP),
  password: primitive(),
  passwordConfirmation: primitive(),
  phone: primitive(),
  callback: object(userCallbackSchema),
  user_alertings: list(object(userAlertingSchema)),
  companyName: primitive(),
  invoiceAddress: custom((v) => v, (v) => (v ? deserialize(cpmAddressSchema, v) : null)),
  nationalIdentifier: primitive(),
});



// WEBPACK FOOTER //
// ./models/schemas/index.js